import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const CreateBrand: React.FC = () => {
  const { user } = useAuth();
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [team, setTeam] = useState('');
  const [logo, setLogo] = useState<File | null>(null);
  const [primaryColor, setPrimaryColor] = useState('#3B82F6');
  const [secondaryColor, setSecondaryColor] = useState('#EF4444');
  const [typography, setTypography] = useState('');
  const [brandStyle, setBrandStyle] = useState<File | null>(null);
  
  // New state for multiple brand assets
  const [brandAssets, setBrandAssets] = useState<File[]>([]);
  const [brandColors, setBrandColors] = useState<string[]>(['#3B82F6', '#EF4444']);
  const [brandFonts, setBrandFonts] = useState<string[]>(['Inter']);
  const [brandGuidelines, setBrandGuidelines] = useState<File[]>([]);
  
  const [uploading, setUploading] = useState(false);
  const [uploadedLogoUrl, setUploadedLogoUrl] = useState<string | null>(null);
  const [uploadedStyleUrl, setUploadedStyleUrl] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  // File handling functions
  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file for the logo');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Logo file size must be less than 5MB');
        return;
      }
      setLogo(file);
      setError(null);
    }
  };

  const handleStyleGuideChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const allowedTypes = ['.pdf', '.png', '.jpg', '.jpeg'];
      const fileExtension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedTypes.includes(fileExtension)) {
        setError('Please select a PDF, PNG, or JPG file for the style guide');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        setError('Style guide file size must be less than 10MB');
        return;
      }
      setBrandStyle(file);
      setError(null);
    }
  };

  // Multiple asset handling functions
  const handleBrandAssetsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    setBrandAssets(prev => [...prev, ...files]);
    setError(null);
  };

  const removeBrandAsset = (index: number) => {
    setBrandAssets(prev => prev.filter((_, i) => i !== index));
  };

  const handleBrandGuidelinesChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []) as File[];
    setBrandGuidelines(prev => [...prev, ...files]);
    setError(null);
  };

  const removeBrandGuideline = (index: number) => {
    setBrandGuidelines(prev => prev.filter((_, i) => i !== index));
  };

  // Color and font management functions
  const addBrandColor = () => {
    setBrandColors(prev => [...prev, '#000000']);
  };

  const removeBrandColor = (index: number) => {
    setBrandColors(prev => prev.filter((_, i) => i !== index));
  };

  const updateBrandColor = (index: number, color: string) => {
    setBrandColors(prev => prev.map((c, i) => i === index ? color : c));
  };

  const addBrandFont = () => {
    setBrandFonts(prev => [...prev, '']);
  };

  const removeBrandFont = (index: number) => {
    setBrandFonts(prev => prev.filter((_, i) => i !== index));
  };

  const updateBrandFont = (index: number, font: string) => {
    setBrandFonts(prev => prev.map((f, i) => i === index ? font : f));
  };

  // File preview functions
  const getFilePreview = (file: File) => {
    if (file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  const getLogoPreview = () => {
    if (logo) {
      return URL.createObjectURL(logo);
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Create Your Brand</h1>
          <p className="text-xl text-gray-600">Build a powerful brand identity</p>
        </div>

        {/* Success Message */}
        {success && (
          <div className="mb-8 bg-green-50 border border-green-200 rounded-2xl p-8 text-center">
            <h3 className="text-2xl font-semibold text-green-900 mb-2">Brand Created Successfully!</h3>
            <p className="text-green-700">Your brand is now ready to use.</p>
          </div>
        )}

        {/* Main Form */}
        {!success && (
          <div className="bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
            <form onSubmit={async (e) => {
              e.preventDefault();
              setError(null);
              setSuccess(false);
              
              let logoUrl = null;
              let styleUrl = null;
              let brandAssetsUrls: string[] = [];
              let brandGuidelinesUrls: string[] = [];
              
              // Upload logo if selected
              if (logo) {
                setUploading(true);
                const { data: logoData, error: logoError } = await supabase.storage
                  .from('brand-logos')
                  .upload(`logos/${Date.now()}_${logo.name}`, logo);
                
                if (logoError) {
                  setError('Logo upload failed: ' + logoError.message);
                  setUploading(false);
                  return;
                }
                logoUrl = logoData?.path ? supabase.storage.from('brand-logos').getPublicUrl(logoData.path).data.publicUrl : null;
              }
              
              // Upload brand style guide if selected
              if (brandStyle) {
                const { data: styleData, error: styleError } = await supabase.storage
                  .from('brand-styles')
                  .upload(`styles/${Date.now()}_${brandStyle.name}`, brandStyle);
                
                if (styleError) {
                  setError('Style guide upload failed: ' + styleError.message);
                  setUploading(false);
                  return;
                }
                styleUrl = styleData?.path ? supabase.storage.from('brand-styles').getPublicUrl(styleData.path).data.publicUrl : null;
              }

              // Upload brand assets if any
              if (brandAssets.length > 0) {
                try {
                  const uploadPromises = brandAssets.map(async (file, index) => {
                    const fileName = `${Date.now()}_${index}_${file.name}`;
                    const { data, error } = await supabase.storage
                      .from('brand-assets')
                      .upload(fileName, file);
                    
                    if (error) throw error;
                    
                    const { data: { publicUrl } } = supabase.storage
                      .from('brand-assets')
                      .getPublicUrl(fileName);
                    
                    return publicUrl;
                  });
                  
                  brandAssetsUrls = await Promise.all(uploadPromises);
                } catch (uploadError) {
                  setError('Failed to upload some brand assets. Please try again.');
                  setUploading(false);
                  return;
                }
              }

              // Upload brand guidelines if any
              if (brandGuidelines.length > 0) {
                try {
                  const uploadPromises = brandGuidelines.map(async (file, index) => {
                    const fileName = `${Date.now()}_${index}_${file.name}`;
                    const { data, error } = await supabase.storage
                      .from('brand-assets')
                      .upload(`guidelines/${fileName}`, file);
                    
                    if (error) throw error;
                    
                    const { data: { publicUrl } } = supabase.storage
                      .from('brand-assets')
                      .getPublicUrl(`guidelines/${fileName}`);
                    
                    return publicUrl;
                  });
                  
                  brandGuidelinesUrls = await Promise.all(uploadPromises);
                } catch (uploadError) {
                  setError('Failed to upload some brand guidelines. Please try again.');
                  setUploading(false);
                  return;
                }
              }
              
              // Insert into brands table
              const { error: dbError } = await supabase.from('brands').insert([
                {
                  name,
                  description: desc,
                  industry: team,
                  logo_url: logoUrl,
                  primary_color: primaryColor,
                  secondary_color: secondaryColor,
                  typography: typography,
                  style_guide_url: styleUrl,
                  brand_assets: brandAssetsUrls,
                  brand_colors: brandColors,
                  brand_fonts: brandFonts,
                  brand_guidelines: brandGuidelinesUrls,
                  user_id: user?.id,
                },
              ]);
              
              if (dbError) {
                setError('Failed to create brand: ' + dbError.message);
                setUploading(false);
                return;
              }
              
              setSuccess(true);
              setTimeout(() => {
                navigate('/dashboard/brands');
              }, 1000);
            }} className="p-8 space-y-8">
              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-2xl p-6">
                  <p className="text-red-700">{error}</p>
                </div>
              )}

              {/* Basic Information */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">Basic Information</h3>
                
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
                  <div className="space-y-4">
                    <label className="block text-lg font-semibold text-gray-900">Brand Name *</label>
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      required
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg"
                      placeholder="Enter your brand name"
                    />
                  </div>
                  
                  <div className="space-y-4">
                    <label className="block text-lg font-semibold text-gray-900">Industry/Team</label>
                    <input
                      type="text"
                      value={team}
                      onChange={e => setTeam(e.target.value)}
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg"
                      placeholder="e.g., Technology, Healthcare, Fashion"
                    />
                  </div>

                  <div className="space-y-4">
                    <label className="block text-lg font-semibold text-gray-900">Typography</label>
                    <input
                      type="text"
                      value={typography}
                      onChange={e => setTypography(e.target.value)}
                      className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg"
                      placeholder="e.g., Inter, Roboto, Helvetica Neue"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-lg font-semibold text-gray-900">Description</label>
                  <textarea
                    value={desc}
                    onChange={e => setDesc(e.target.value)}
                    rows={4}
                    className="w-full px-6 py-4 border-2 border-gray-200 rounded-2xl focus:ring-4 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg resize-none"
                    placeholder="Describe your brand and what it represents..."
                  />
                </div>
              </div>

              {/* Visual Identity */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">Visual Identity</h3>
                
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                  {/* Logo Upload */}
                  <div className="space-y-4">
                    <label className="block text-lg font-semibold text-gray-900">Brand Logo</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-primary transition-all duration-300">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleLogoChange}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label htmlFor="logo-upload" className="cursor-pointer block">
                        {getLogoPreview() ? (
                          <div className="mb-4">
                            <img 
                              src={getLogoPreview()!} 
                              alt="Logo preview"
                              className="w-24 h-24 object-contain mx-auto rounded-xl shadow-lg"
                            />
                          </div>
                        ) : (
                          <div className="mx-auto w-24 h-24 bg-gradient-to-r from-gray-100 to-gray-200 rounded-2xl flex items-center justify-center mb-4">
                            <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        )}
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">
                          {getLogoPreview() ? 'Change Logo' : 'Upload Logo'}
                        </h4>
                        <p className="text-gray-600 mb-4">PNG, JPG, or GIF • Max 5MB</p>
                        <div className="inline-flex items-center px-6 py-3 bg-primary text-white rounded-xl font-semibold hover:bg-primary-dark transition-colors">
                          Choose File
                        </div>
                      </label>
                    </div>
                    {logo && (
                      <div className="flex items-center space-x-3 p-4 bg-green-50 rounded-xl border border-green-200">
                        <span className="text-green-800 font-medium">Selected: {logo.name}</span>
                      </div>
                    )}
                  </div>

                  {/* Brand Style Guide */}
                  <div className="space-y-4">
                    <label className="block text-lg font-semibold text-gray-900">Style Guide Document</label>
                    <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-primary transition-all duration-300">
                      <input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        onChange={handleStyleGuideChange}
                        className="hidden"
                        id="style-guide-upload"
                      />
                      <label htmlFor="style-guide-upload" className="cursor-pointer block">
                        <div className="mx-auto w-24 h-24 bg-gradient-to-r from-orange-100 to-orange-200 rounded-2xl flex items-center justify-center mb-4">
                          <svg className="w-12 h-12 text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                        </div>
                        <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload Style Guide</h4>
                        <p className="text-gray-600 mb-4">PDF, PNG, or JPG • Max 10MB</p>
                        <div className="inline-flex items-center px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600 transition-colors">
                          Choose File
                        </div>
                      </label>
                    </div>
                    
                    {brandStyle && (
                      <div className="flex items-center space-x-3 p-4 bg-orange-50 rounded-xl border border-orange-200">
                        <span className="text-orange-800 font-medium">Selected: {brandStyle.name}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Color Selection */}
                <div className="space-y-6">
                  <h4 className="text-xl font-semibold text-gray-900">Brand Colors</h4>
                  <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <label className="block text-lg font-semibold text-gray-900">Primary Color</label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="color"
                          value={primaryColor}
                          onChange={e => setPrimaryColor(e.target.value)}
                          className="w-16 h-16 border-2 border-gray-200 rounded-2xl cursor-pointer"
                        />
                        <input
                          type="text"
                          value={primaryColor}
                          onChange={e => setPrimaryColor(e.target.value)}
                          className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 font-mono"
                          placeholder="#3B82F6"
                        />
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <label className="block text-lg font-semibold text-gray-900">Secondary Color</label>
                      <div className="flex items-center space-x-4">
                        <input
                          type="color"
                          value={secondaryColor}
                          onChange={e => setSecondaryColor(e.target.value)}
                          className="w-16 h-16 border-2 border-gray-200 rounded-2xl cursor-pointer"
                        />
                        <input
                          type="text"
                          value={secondaryColor}
                          onChange={e => setSecondaryColor(e.target.value)}
                          className="flex-1 px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 font-mono"
                          placeholder="#EF4444"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Brand Assets Section */}
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-gray-900">Brand Assets</h3>
                
                {/* Multiple Brand Assets Upload */}
                <div className="space-y-6">
                  <h4 className="text-xl font-semibold text-gray-900">Additional Brand Assets</h4>
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-primary transition-all duration-300">
                    <input
                      type="file"
                      multiple
                      accept="image/*,.pdf,.doc,.docx,.psd,.ai,.sketch,.zip,.rar"
                      onChange={handleBrandAssetsChange}
                      className="hidden"
                      id="brand-assets-upload"
                    />
                    <label htmlFor="brand-assets-upload" className="cursor-pointer block">
                      <div className="mx-auto w-24 h-24 bg-gradient-to-r from-green-100 to-green-200 rounded-2xl flex items-center justify-center mb-6">
                        <svg className="w-12 h-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload Brand Assets</h4>
                      <p className="text-gray-600 mb-4">Icons, patterns, templates, and other brand elements</p>
                      <div className="inline-flex items-center px-6 py-3 bg-green-500 text-white rounded-xl font-semibold hover:bg-green-600 transition-colors">
                        Choose Files
                      </div>
                    </label>
                  </div>

                  {/* Brand Assets Previews */}
                  {brandAssets.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h5 className="text-lg font-semibold text-gray-900">Uploaded Assets</h5>
                        <span className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-full font-medium">
                          {brandAssets.length} file{brandAssets.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {brandAssets.map((file, index) => (
                          <div key={index} className="relative bg-white rounded-xl p-4 border border-gray-200 hover:shadow-lg transition-all duration-300 group">
                            {getFilePreview(file) ? (
                              <div className="mb-3">
                                <img 
                                  src={getFilePreview(file)!} 
                                  alt={`Asset ${index + 1}`}
                                  className="w-full h-32 object-cover rounded-lg"
                                />
                              </div>
                            ) : (
                              <div className="mb-3 flex items-center justify-center h-32 bg-gray-100 rounded-lg">
                                <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                              </div>
                            )}
                            <div className="text-center mb-3">
                              <p className="text-sm font-medium text-gray-900 truncate mb-1">{file.name}</p>
                              <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeBrandAsset(index)}
                              className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all duration-300 opacity-0 group-hover:opacity-100"
                              title="Remove file"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Brand Colors Array */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xl font-semibold text-gray-900">Brand Color Palette</h4>
                    <button
                      type="button"
                      onClick={addBrandColor}
                      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors"
                    >
                      Add Color
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {brandColors.map((color, index) => (
                      <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
                        <input
                          type="color"
                          value={color}
                          onChange={e => updateBrandColor(index, e.target.value)}
                          className="w-12 h-12 border-2 border-gray-200 rounded-xl cursor-pointer"
                        />
                        <input
                          type="text"
                          value={color}
                          onChange={e => updateBrandColor(index, e.target.value)}
                          className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 font-mono text-sm"
                          placeholder="#000000"
                        />
                        {brandColors.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeBrandColor(index)}
                            className="w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"
                            title="Remove color"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Brand Fonts Array */}
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xl font-semibold text-gray-900">Brand Typography</h4>
                    <button
                      type="button"
                      onClick={addBrandFont}
                      className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-xl font-medium hover:bg-primary-dark transition-colors"
                    >
                      Add Font
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {brandFonts.map((font, index) => (
                      <div key={index} className="flex items-center space-x-4 p-4 bg-gray-50 rounded-xl">
                        <input
                          type="text"
                          value={font}
                          onChange={e => updateBrandFont(index, e.target.value)}
                          className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all duration-300 text-lg"
                          placeholder="e.g., Inter, Roboto, Helvetica Neue"
                        />
                        {brandFonts.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeBrandFont(index)}
                            className="w-8 h-8 bg-red-500 text-white rounded-lg flex items-center justify-center hover:bg-red-600 transition-colors"
                            title="Remove font"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Brand Guidelines Upload */}
                <div className="space-y-6">
                  <h4 className="text-xl font-semibold text-gray-900">Brand Guidelines Documents</h4>
                  <div className="border-2 border-dashed border-gray-300 rounded-2xl p-8 text-center hover:border-primary transition-all duration-300">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.psd,.ai,.sketch"
                      onChange={handleBrandGuidelinesChange}
                      className="hidden"
                      id="brand-guidelines-upload"
                    />
                    <label htmlFor="brand-guidelines-upload" className="cursor-pointer block">
                      <div className="mx-auto w-24 h-24 bg-gradient-to-r from-blue-100 to-blue-200 rounded-2xl flex items-center justify-center mb-6">
                        <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-2">Upload Guidelines</h4>
                      <p className="text-gray-600 mb-4">Brand manuals, style guides, and documentation</p>
                      <div className="inline-flex items-center px-6 py-3 bg-blue-500 text-white rounded-xl font-semibold hover:bg-blue-600 transition-colors">
                        Choose Files
                      </div>
                    </label>
                  </div>

                  {/* Brand Guidelines Previews */}
                  {brandGuidelines.length > 0 && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <h5 className="text-lg font-semibold text-gray-900">Uploaded Guidelines</h5>
                        <span className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-full font-medium">
                          {brandGuidelines.length} file{brandGuidelines.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {brandGuidelines.map((file, index) => (
                          <div key={index} className="relative bg-white rounded-xl p-4 border border-gray-200 hover:shadow-lg transition-all duration-300 group">
                            <div className="mb-3 flex items-center justify-center h-32 bg-blue-100 rounded-lg">
                              <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                            </div>
                            <div className="text-center mb-3">
                              <p className="text-sm font-medium text-gray-900 truncate mb-1">{file.name}</p>
                              <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => removeBrandGuideline(index)}
                              className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-all duration-300 opacity-0 group-hover:opacity-100"
                              title="Remove file"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Submit Button */}
              <div className="pt-8">
                <button
                  type="submit"
                  disabled={uploading}
                  className="w-full bg-gradient-to-r from-primary to-primary-dark text-white px-8 py-6 rounded-2xl font-bold text-xl hover:from-primary-dark hover:to-primary transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
                >
                  {uploading ? (
                    <div className="flex items-center justify-center space-x-3">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
                      <span>Creating Your Brand...</span>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center space-x-3">
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                      <span>Create Brand</span>
                    </div>
                  )}
                </button>
                
                <p className="text-center text-gray-500 mt-6">
                  By creating this brand, you agree to our terms of service and privacy policy.
                </p>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateBrand;
